import { useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Building2,
  Calendar,
  Check,
  FileText,
  Loader2,
  Phone,
  User,
  X,
} from 'lucide-react';
import { Alert, AlertDescription } from '../../../../../components/ui/alert';
import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { formatCnpj, formatCpf, formatDateBR, formatPhone } from '../../../../../utils';
import { EntregoRegistration } from '../types';

interface RegistrationDetailsProps {
  registration: EntregoRegistration;
  isUpdating: boolean;
  updateSuccess: boolean;
  updateError: string | null;
  onUpdateStatus: (ativo: boolean) => void;
  onClose: () => void;
}


export function RegistrationDetails({
  registration,
  isUpdating,
  updateSuccess,
  updateError,
  onUpdateStatus,
  onClose,
}: RegistrationDetailsProps) {
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const handleDeactivateClick = () => {
    setShowDeactivateConfirm(true);
  };

  const handleConfirmDeactivate = () => {
    setShowDeactivateConfirm(false);
    onUpdateStatus(false);
  };

  const documento = registration.cpf
    ? formatCpf(registration.cpf)
    : registration.cnpj
      ? formatCnpj(registration.cnpj)
      : 'Não informado';

  const documentoLabel = registration.cpf
    ? 'CPF'
    : registration.cnpj
      ? 'CNPJ'
      : 'Documento';

  return (
    <Card className="animate-in slide-in-from-top-4 fade-in-50 duration-300">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Detalhes do Cadastro
          </CardTitle>
          <CardDescription>
            Informações do entregador encontrado
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {updateSuccess && (
          <Alert className="animate-in fade-in-50 duration-200 border-green-200 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              Status atualizado com sucesso!
            </AlertDescription>
          </Alert>
        )}

        {updateError && (
          <Alert
            variant="destructive"
            className="animate-in fade-in-50 duration-200"
          >
            <AlertDescription>{updateError}</AlertDescription>
          </Alert>
        )}

        {registration.naoAprovadoAzumi && (
          <Alert className="animate-in fade-in-50 duration-200 border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-700">
              Cadastro ainda não aprovado pela Azumi
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Nome</p>
            <p className="flex items-center gap-2 text-foreground">
              <User className="h-4 w-4 text-muted-foreground" />
              {registration.nomeCompleto}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              {documentoLabel}
            </p>
            <p className="flex items-center gap-2 text-foreground">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {documento}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              Telefone
            </p>
            <p className="flex items-center gap-2 text-foreground">
              <Phone className="h-4 w-4 text-muted-foreground" />
              {registration.telefone
                ? formatPhone(registration.telefone)
                : 'Não informado'}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              Operação
            </p>
            <p className="flex items-center gap-2 text-foreground">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {registration.ops || 'Não informado'}
            </p>
          </div>

          {registration.dataNascimento && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Data de Nascimento
              </p>
              <p className="flex items-center gap-2 text-foreground">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                {formatDateBR(registration.dataNascimento)}
              </p>
            </div>
          )}

          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Status</p>
            <Badge
              variant={registration.ativo ? 'default' : 'destructive'}
              className="animate-in zoom-in-50 duration-200"
            >
              {registration.ativo ? 'Ativo' : 'Bloqueado'}
            </Badge>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end gap-2 border-t pt-4">
        {registration.ativo ? (
          <Button
            variant="destructive"
            onClick={handleDeactivateClick}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Desativando...
              </>
            ) : (
              <>
                <Ban className="mr-2 h-4 w-4" />
                Desativar Cadastro
              </>
            )}
          </Button>
        ) : (
          <Button onClick={() => onUpdateStatus(true)} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ativando...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Ativar Cadastro
              </>
            )}
          </Button>
        )}
      </CardFooter>

      <Dialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Desativação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja desativar o cadastro de{' '}
              <span className="font-semibold">{registration.nomeCompleto}</span>?
              Esta ação pode ser revertida posteriormente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeactivateConfirm(false)}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeactivate}>
              Desativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
